import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FileSignature,
  FileText,
  PlusCircle,
  Search,
  Calendar,
  Eye,
  Copy,
  Loader2,
  Pencil,
  Download,
  ListChecks,
  Link2,
  Trash2,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { vendorContractApi } from "../../services/vendorContractService";
import { downloadContractPdf } from "../../utils/vendorContractPdf";
import { useToastContext } from "../../contexts/ToastContext";
import { useConfirm } from "../../contexts/ConfirmContext";
import {
  VendorPageHeader,
  VendorGuideCallout,
  VendorEmptyState,
} from "../../components/vendor/guidance";
import { VENDOR_PAGE_GUIDES } from "../../utils/vendorPageGuides";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "sent", label: "Sent" },
  { key: "viewed", label: "Viewed" },
  { key: "signed", label: "Signed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "expired", label: "Expired" },
];

const statusColor = (status) => {
  const map = {
    draft: "bg-gray-100 text-gray-700",
    sent: "bg-surface-muted text-ink",
    viewed: "bg-amber-100 text-amber-800",
    signed: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-red-100 text-red-700",
    expired: "bg-orange-100 text-orange-800",
  };
  return map[status] || "bg-gray-100 text-gray-700";
};

const VendorContracts = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { success, error: showError } = useToastContext();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["vendor-contracts", searchTerm, status],
    queryFn: () =>
      vendorContractApi.list({
        search: searchTerm || undefined,
        status: status || undefined,
        per_page: 30,
      }),
  });

  const contracts = data?.data || [];

  const handleDownload = useCallback(
    async (contractId) => {
      setDownloadingId(contractId);
      try {
        const res = await vendorContractApi.getOne(contractId);
        const contract = res?.data;
        if (!contract) throw new Error("Contract not found");
        await downloadContractPdf(contract);
        success("Downloaded", "Contract PDF saved to your device.");
      } catch (e) {
        console.error(e);
        showError(
          "Download failed",
          e?.response?.data?.message || "Could not create PDF. Try again.",
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [success, showError],
  );

  const canDeleteContract = (contract) =>
    String(contract?.status || "").toLowerCase() !== "signed" &&
    !contract?.booking_id &&
    !contract?.has_booking;

  const handleDelete = useCallback(
    async (contract) => {
      if (String(contract?.status || "").toLowerCase() === "signed") {
        showError("Cannot delete", "Signed contracts cannot be deleted.");
        return;
      }
      if (contract?.booking_id || contract?.has_booking) {
        showError(
          "Cannot delete",
          "This contract is linked to a booking. Open the booking to manage it.",
        );
        return;
      }
      const ok = await confirm({
        title: "Delete contract?",
        message: `Delete contract "${contract.title || contract.contract_number || ""}"? This cannot be undone.`,
        confirmLabel: "Delete",
        variant: "danger",
      });
      if (!ok) return;
      setDeletingId(contract.id);
      try {
        await vendorContractApi.remove(contract.id);
        success(null, "Contract deleted.");
        queryClient.invalidateQueries({ queryKey: ["vendor-contracts"] });
      } catch (e) {
        showError(null, e?.response?.data?.message || "Failed to delete contract.");
      } finally {
        setDeletingId(null);
      }
    },
    [confirm, queryClient, showError, success],
  );

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Contracts"
        subtitle="Create contracts from templates, draft with AI, and collect online signatures."
        actions={
          <Link
            to="/vendor/contracts/create"
            className="inline-flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium rounded-md shadow-sm bg-ink text-white hover:bg-black"
          >
            <PlusCircle className="w-4 h-4" />
            New contract
          </Link>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.contracts.title}
        steps={VENDOR_PAGE_GUIDES.contracts.steps}
        icon={ListChecks}
      />

      <div className="rounded-lg border border-border bg-surface shadow-sm overflow-hidden">
        <div className="border-b border-border px-4 py-3 sm:px-5 space-y-3">
          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
            role="tablist"
            aria-label="Contract status"
          >
            {STATUS_TABS.map((tab) => {
              const isActive = status === tab.key;
              return (
                <button
                  key={tab.key || "all"}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setStatus(tab.key)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? "bg-surface text-ink shadow-sm" : "text-ink-secondary hover:text-gray-900"}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search contracts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full !pl-10 pr-4 py-1.5 text-xs rounded-xl border border-border bg-surface text-ink placeholder:text-ink-tertiary focus:outline-none focus:ring-2 focus:ring-ink/40 focus:border-ink"
            />
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
              <p className="text-sm text-ink-tertiary">Loading contracts…</p>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm">
              Failed to load contracts.
            </div>
          ) : contracts.length === 0 ? (
            <VendorEmptyState
              icon={FileSignature}
              title="No contracts yet"
              description="Start from a template or draft with AI, sign as the vendor, then send a link for the client to sign."
              tips={[
                "Pick a template or generate with AI",
                "Add your logo and vendor signature",
                "Email the signing link — no client account needed",
              ]}
              actionLabel="Create contract"
              actionTo="/vendor/contracts/create"
            />
          ) : (
            <div className="space-y-3">
              {contracts.map((c) => (
                <div
                  key={c.id}
                  className="product-surface-muted p-3 sm:p-4 transition-colors hover:border-ink"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0 flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-gray-400">
                        <FileSignature className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="font-semibold text-ink truncate">
                            {c.title}
                          </p>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColor(c.status)}`}
                          >
                            {c.status}
                          </span>
                        </div>
                        {(c.booking_id ||
                          c.has_booking ||
                          c.has_invoice ||
                          c.primary_invoice_id) && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {(c.booking_id || c.has_booking) && (
                              <Link
                                to={`/vendor/bookings/${c.booking_id || c.booking?.id}`}
                                className="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium text-teal-800 hover:bg-teal-100"
                                title="Open linked booking"
                              >
                                <Link2 className="w-3 h-3" />
                                {c.booking_reference || c.booking?.booking_reference
                                  ? `#${c.booking_reference || c.booking.booking_reference}`
                                  : "Linked booking"}
                              </Link>
                            )}
                            {(c.has_invoice || c.primary_invoice_id) && (
                              <Link
                                to={`/vendor/invoices/${c.primary_invoice_id}`}
                                className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-800 hover:bg-sky-100"
                                title="Open linked invoice"
                              >
                                <FileText className="w-3 h-3" />
                                Linked invoice
                              </Link>
                            )}
                          </div>
                        )}
                        <p className="text-sm text-ink-tertiary">
                          {c.contract_number} · {c.client_name}
                          {c.client_email ? ` · ${c.client_email}` : ""}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-ink-tertiary">
                          <Calendar className="w-3.5 h-3.5 shrink-0" />
                          {c.created_at
                            ? new Date(c.created_at).toLocaleDateString(
                                undefined,
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                },
                              )
                            : "—"}
                          {c.signed_at && (
                            <span className="text-emerald-700">
                              · Signed{" "}
                              {new Date(c.signed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0 sm:pl-12">
                      {!["signed", "cancelled"].includes(
                        String(c.status || "").toLowerCase(),
                      ) && (
                        <Link
                          to={`/vendor/contracts/${c.id}/edit`}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-border bg-surface text-gray-800 rounded-md hover:bg-surface-muted"
                          title="Edit contract"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </Link>
                      )}
                      <Link
                        to={`/vendor/contracts/create?from=${c.id}`}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-border bg-surface text-gray-800 rounded-md hover:bg-surface-muted"
                        title="Copy for another client"
                      >
                        <Copy className="w-4 h-4" />
                        Copy
                      </Link>
                      <Link
                        to={`/vendor/contracts/${c.id}`}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-border bg-surface text-gray-800 rounded-md hover:bg-surface-muted"
                      >
                        <Eye className="w-4 h-4" />
                        Open
                      </Link>
                      {canDeleteContract(c) && (
                        <button
                          type="button"
                          onClick={() => handleDelete(c)}
                          disabled={deletingId === c.id}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-red-200 bg-surface text-red-700 rounded-md hover:bg-red-50 disabled:opacity-60"
                          title="Delete contract"
                        >
                          {deletingId === c.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                          Delete
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDownload(c.id)}
                        disabled={downloadingId === c.id}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-60"
                        title="Download PDF"
                      >
                        {downloadingId === c.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">PDF</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorContracts;
